import { useEffect, useState } from 'react';
import { Alert, Select, Tag } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { AxiosError } from 'axios';
import { GithubRepositoryMapping } from '../../../../types/Github';
import { useGetSpaceRepositoryOptions, useSetSpaceRepositoriesMutation } from '../../../../serverState/github';
import { BaseButton } from '../../../../components/UI/BaseButton';

/**
 * Which GitHub repositories are connected to this space, chosen by the space's OWNER.
 *
 * Before this, connecting a repository to a space lived only in Settings, Integrations,
 * behind organization ADMIN - so a space owner who was not an admin could configure how
 * pull requests move their tasks and not which repository those pull requests come from.
 * The write was always gated on space ownership underneath that admin guard, so this is
 * the same authority reached from the space it applies to.
 *
 * WHAT IT WILL NOT OFFER: a repository already connected to another space. That is
 * enforced server-side, and the list simply does not contain one - taking it would
 * detach GitHub from a space this owner may have no authority over.
 *
 * The save is a full replace, so unticking a repository detaches it, and detaching drops
 * the task links built for it. That is why this has its own Save rather than reacting to
 * each tick.
 *
 * DESIGN: the multi-select renders its selections as tags, which is what this row looked
 * like when it was read-only, so a non-owner and an owner see the same shape.
 */
export type SpaceSettingsGithubRepositoriesProps = {
  spaceId: number | null;
  organizationId: number | null;
  /** True for anyone who is not a space owner: read-only, and the OWNER-guarded read is never fired. */
  isDisabled?: boolean;
  /**
   * The repositories the organization-scoped list reports for this space. Read-only
   * fallback, and the value this renders before the owner-only query settles, so the row
   * does not start empty for someone who does have repositories connected.
   */
  mappedRepositories: GithubRepositoryMapping[];
  onDirtyChange?: (dirty: boolean) => void;
};

const label = (repository: GithubRepositoryMapping) => repository.fullName;

export const SpaceSettingsGithubRepositories = ({
  spaceId,
  organizationId,
  isDisabled,
  mappedRepositories,
  onDirtyChange,
}: SpaceSettingsGithubRepositoriesProps) => {
  // Not fired for a non-owner: the endpoint is spaceGuard(OWNER) and would 403. The
  // read-only view below has everything it needs from `mappedRepositories`.
  const optionsQuery = useGetSpaceRepositoryOptions(spaceId, !isDisabled);
  const saveMutation = useSetSpaceRepositoriesMutation();

  const [selected, setSelected] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Until the owner-only read settles, show what the organization-scoped list has already
  // told the tab. Starting empty would render "none connected" to a space that has
  // repositories connected, which is the one thing this row must never say wrongly.
  const attached = optionsQuery.data?.attached ?? mappedRepositories;
  const available = optionsQuery.data?.available ?? [];
  const attachedIds = attached.map((repository) => repository.repoId);

  // Reseed whenever the server's view changes, matching how the rules table beside this
  // one handles its own save: the mutation invalidates, the refetch lands here.
  useEffect(() => {
    if (!optionsQuery.data) return;
    setSelected(optionsQuery.data.attached.map((repository) => repository.repoId));
  }, [optionsQuery.data]);

  const current = selected ?? attachedIds;
  const dirty =
    !!selected && (selected.length !== attachedIds.length || selected.some((repoId) => !attachedIds.includes(repoId)));

  useEffect(() => {
    onDirtyChange?.(dirty);
    // Unticking the box and then leaving must not leave the modal thinking there is
    // something to save, so the flag is cleared on the way out.
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const onSave = async () => {
    if (!spaceId || !organizationId || !selected) return;
    setError(null);
    try {
      await saveMutation.mutateAsync({ spaceId, organizationId, repoIds: selected });
    } catch (err) {
      // The 409 is the one case where the server's own sentence is the useful part: it
      // says an admin has this repository pointed at another space, which is neither
      // something to retry nor something this owner can fix from here.
      const response = (err as AxiosError<{ error?: string }>).response;
      setError(
        response?.status === 409
          ? (response.data?.error ?? 'That repository is already connected to another space.')
          : 'Could not save the connected repositories. Please try again.'
      );
    }
  };

  const heading = <span className="text-xs font-medium uppercase tracking-wide text-neutral-900">Repositories</span>;

  const tags = (repositories: GithubRepositoryMapping[]) =>
    repositories.length === 0 ? (
      <span className="text-sm text-neutral-900">None connected</span>
    ) : (
      <span className="flex flex-wrap items-center gap-1">
        {repositories.map((repository) => (
          <Tag key={repository.repoId} className="m-0">
            {label(repository)}
          </Tag>
        ))}
      </span>
    );

  if (isDisabled) {
    return (
      <div className="mt-5 w-full flex flex-wrap items-center gap-x-4 gap-y-2">
        {heading}
        {tags(mappedRepositories)}
      </div>
    );
  }

  // An errored read must not present an empty picker: ticking nothing and saving it would
  // detach every repository this space has. Fall back to the read-only tags, which come
  // from the organization-scoped list the tab has already loaded.
  if (optionsQuery.isError) {
    return (
      <div className="mt-5 w-full flex flex-wrap items-center gap-x-4 gap-y-2">
        {heading}
        {tags(mappedRepositories)}
        <BaseButton type="secondary" size="medium" onClick={() => void optionsQuery.refetch()}>
          Retry
        </BaseButton>
      </div>
    );
  }

  const options = [...attached, ...available]
    .sort((a, b) => label(a).localeCompare(label(b)))
    .map((repository) => ({
      value: repository.repoId,
      label: (
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{label(repository)}</span>
          {repository.private && (
            <Tag className="m-0 shrink-0" icon={<LockOutlined />}>
              Private
            </Tag>
          )}
        </span>
      ),
      // What the collapsed tag and the type-ahead match on. Without it the tag renders
      // the whole label node, Private badge included.
      title: label(repository),
    }));

  return (
    <div className="mt-5 w-full">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {heading}
        <Select
          className="min-w-0 flex-1"
          mode="multiple"
          allowClear={true}
          optionFilterProp="title"
          // Both point at the plain name. The option's own label carries a Private badge
          // beside it, which belongs in the open list and not inside a collapsed tag.
          optionLabelProp="title"
          placeholder={options.length === 0 ? 'No repository is free to connect' : 'Connect a repository to this space'}
          value={current}
          options={options}
          disabled={optionsQuery.isLoading || (options.length === 0 && current.length === 0)}
          loading={optionsQuery.isLoading || saveMutation.isPending}
          onChange={(value: string[]) => setSelected(value)}
        />
        {dirty && (
          <BaseButton type="primary" size="medium" disabled={saveMutation.isPending} onClick={() => void onSave()}>
            Save
          </BaseButton>
        )}
      </div>

      {/* Said here rather than only in the placeholder: an owner looking at an empty
          picker needs to know whether the organization has nothing connected or whether
          every repository is already spoken for. */}
      {!optionsQuery.isLoading && options.length === 0 && (
        <p className="m-0 mt-2 text-xs leading-relaxed text-neutral-900">
          Every repository this organization has connected is already used by another space. An organization admin can
          add more under Settings, Integrations.
        </p>
      )}

      {error && <Alert className="mt-2 w-full" type="error" showIcon={true} message={error} />}
    </div>
  );
};
